/**
 * The server's Connections readiness projection (`readiness` on `GET /v1/provider-keys`).
 *
 * Built by `auteur_cloud/connections/readiness.py`. The browser keeps no list of what
 * runs: every "ready", "planned" or "unavailable" shown in Settings comes from here,
 * for the one workspace the customer selected. Nothing is cached between reads, so a
 * deleted key or a revoked approval shows on the next load.
 */

export type ValidationState =
  | 'valid'
  | 'not_recently_checked'
  | 'not_checked'
  | 'cannot_verify'
  | 'unreachable'
  | 'invalid';

export interface ReadinessStatus {
  code: string;
  label: string;
  note?: string;
  /** Set where one row has several separate capabilities (YouTube: analytics, upload, bridge_upload). */
  capability?: string;
}

export interface ReadinessKey {
  key_id: string;
  label: string;
  masked_hint: string;
  validation: ValidationState;
  checked_at: string | null;
  /** null: no workspace selected, or its approvals could not be read. */
  authorized_for_workspace: boolean | null;
}

export interface ReadinessOperation {
  operation: string;
  adapter: 'implemented' | 'not_built';
  stored: boolean;
  validated: ValidationState | null;
  authorized_for_workspace: boolean | null;
  operation_available: boolean;
  planned: ReadinessStatus | null;
  unavailable: ReadinessStatus | null;
}

export interface ReadinessProvider {
  id: string;
  label: string;
  route: 'api_key';
  verification: 'probe' | 'not_available' | string;
  stored: boolean;
  validated: ValidationState | null;
  authorized_for_workspace: boolean | null;
  operation_available: boolean;
  keys: ReadinessKey[];
  operations: ReadinessOperation[];
  planned: ReadinessStatus[];
}

export interface ReadinessLane {
  lane: string;
  state: string;
  reason: string | null;
  checked_at: string | null;
}

export interface Readiness {
  schema: string;
  evaluated_at: string;
  workspace: { workspace_id: string | null; approvals: 'read' | 'not_selected' | 'unavailable' };
  validator: { enabled: boolean };
  providers: ReadinessProvider[];
  planned: { id: string; statuses: ReadinessStatus[] }[];
  lanes: ReadinessLane[] | null;
}

/** Read `readiness` off a provider-keys payload; null when the server sent none or it is malformed. */
export function readinessFrom(payload: unknown): Readiness | null {
  if (!payload || typeof payload !== 'object') return null;
  const value = (payload as { readiness?: unknown }).readiness;
  if (!value || typeof value !== 'object') return null;
  const record = value as Partial<Readiness>;
  if (!Array.isArray(record.providers) || !Array.isArray(record.planned) || !record.workspace || !record.validator) {
    return null;
  }
  return record as Readiness;
}

function formatTime(raw: string | null): string {
  if (!raw) return 'an unknown time';
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return 'an unknown time';
  return new Date(parsed).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

/** IN-15: "Not checked", "Valid as of …", "Cannot verify"; an unsupported probe is never "invalid". */
export function validationLabel(state: ValidationState | null, checkedAt: string | null = null): string {
  switch (state) {
    case 'valid': return `Valid as of ${formatTime(checkedAt)}`;
    case 'not_recently_checked': return 'Not recently checked';
    case 'not_checked': return 'Not checked';
    case 'cannot_verify': return 'Cannot verify automatically';
    case 'unreachable': return 'Provider unreachable';
    case 'invalid': return 'Rejected by provider';
    default: return 'No key';
  }
}

export function workspaceAuthLabel(value: boolean | null): string {
  if (value === true) return 'Allowed in this workspace';
  if (value === false) return 'Not allowed in this workspace';
  return 'Workspace permission not known';
}
